import usePublicRuntimeConfig from 'modules/common/hooks/usePublicRuntimeConfig';
import { Link } from 'modules/i18n';
import useTranslation from 'next-translate/useTranslation';
import getConfig from 'next/config';
import { SiLinkedin, SiWhatsapp, SiYoutube } from 'react-icons/si';
import { FaMapMarkerAlt, FaFileAlt, FaUsers, FaInfoCircle } from 'react-icons/fa';

const { publicRuntimeConfig } = getConfig();

export default function Footer() {
  const { t } = useTranslation();

  const { linkedin, whatsappDorothee, youtubeChannelUrl } = usePublicRuntimeConfig();

  const footerSections = {
    cvtheque: {
      title: 'CVThèque',
      icon: <FaMapMarkerAlt className="h-5 w-5 text-primary mb-2" />,
      links: [
        { name: 'CVThèque Paris', href: '/cvtheque/ville/Paris' },
        { name: 'CVThèque Nice', href: '/cvtheque/ville/Nice' },
        { name: 'CVThèque Lyon', href: '/cvtheque/ville/Lyon' },
        { name: 'CVThèque Nantes', href: '/cvtheque/ville/Nantes' },
        { name: 'Toute la CVThèque', href: '/cvtheque' },
      ],
    },
    resources: {
      title: 'Ressources',
      icon: <FaFileAlt className="h-5 w-5 text-primary mb-2" />,
      links: [
        { name: 'Blog', href: '/blog' },
        { name: 'Test du recruteur indépendant', href: '/test-recruteur-independant' },
        { name: 'FAQ', href: '/#faq' },
        { name: 'Guides', href: youtubeChannelUrl },
      ],
    },
    platform: {
      title: 'Plateforme',
      icon: <FaUsers className="h-5 w-5 text-primary mb-2" />,
      links: [
        { name: 'Ambassadeurs', href: '/contact' },
        { name: 'Contact', href: '/contact' },
        { name: 'Recruteurs', href: '/recruiters' },
        // { name: 'Comment ça marche', href: '/how-it-works' },
        { name: 'Abonnement', href: '/abonnement' },
      ],
    },
    legal: {
      title: 'Informations légales',
      icon: <FaInfoCircle className="h-5 w-5 text-primary mb-2" />,
      links: [
        { name: t('footer:privacy-policy'), href: '/privacy-policy' },
        { name: t('footer:terms'), href: '/terms-of-use' },
        { name: t('footer:general-conditions-of-sales'), href: '/general-conditions-of-sales' },
        { name: 'Mentions légales', href: '/legal-notice' },
      ],
    },
  };

  const socialLinks = [
    {
      name: 'LinkedIn',
      href: linkedin,
      icon: (props: any) => <SiLinkedin {...props} />,
      target: '_blank',
      rel: 'noopener noreferrer nofollow',
    },
    {
      name: 'WhatsApp',
      href: whatsappDorothee,
      icon: (props: any) => <SiWhatsapp {...props} />,
      target: '_blank',
      rel: 'noopener noreferrer nofollow',
    },
    {
      name: 'Youtube',
      href: youtubeChannelUrl,
      icon: (props: any) => <SiYoutube {...props} />,
      target: '_blank',
      rel: 'noopener noreferrer nofollow',
    },
  ];

  return (
    <footer className="bg-gray-50 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        {/* Main footer sections */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {Object.entries(footerSections).map(([key, section]) => (
            <div key={key}>
              <div className="flex items-center mb-4">
                {section.icon}
                <h3 className="ml-2 text-lg font-semibold text-gray-900">{section.title}</h3>
              </div>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.name} className="flex items-center">
                    <span className="text-primary mr-2">→</span>
                    <Link href={link.href} prefetch={false} className="text-sm text-gray-600 hover:text-primary transition-colors">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8" />

        {/* Company info and social links */}
        <div className="flex flex-col sm:flex-row justify-between items-center">
          <div className="mb-4 sm:mb-0">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{publicRuntimeConfig.siteName}</h2>
            <p className="text-sm text-gray-600">La plateforme de collaboration pour les recruteurs</p>
          </div>

          {/* Social links */}
          <div className="flex space-x-4">
            {socialLinks.map(({ name, icon: Icon, href, ...item }) => (
              <a key={name} href={href} className="text-gray-400 hover:text-primary transition-colors" aria-label={name} {...item}>
                <Icon className="h-6 w-6" />
              </a>
            ))}
          </div>
        </div>

        {/* Bottom copyright */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <p className="text-center text-sm text-gray-500">
            &copy; depuis 2023{' '}
            <a href={publicRuntimeConfig.siteUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              {publicRuntimeConfig.siteName}
            </a>
            . {t('footer:copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
}
